






/**
 * Allows to make request using the TomTom's
 * [Search API - Structured Geocode](https://developer.tomtom.com/search-api/search-api-documentation-geocoding/structured-geocode).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "StructuredGeocode/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "StructuredGeocode/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * @class StructuredGeocode
 * @uses Chain
 * @module Services
 * @uses KeyMixin
 * @uses LimitMixin
 * @uses OffsetMixin
 * @uses LanguageMixin
 * @uses ProtocolMixin
 * @uses BestResultMixin
 * @constructor
 *
 * @param {Object} [options] Options to be passed to the structured geocoding call,
 *     or Array of such options objects to perform batch request.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 * @param {Boolean}  [options.bestResult=false] Return only one result, the best match result
 *
 * @param {String} [options.key] An API Key issued from <a href="https://developer.tomtom.com">Developer Portal</a>
 * @param {String} options.countryCode 2 or 3 country code letters (e.g.: FR, ES).
 *     **This option is required** to be defined here or by using the
 *     {{#crossLink "StructuredGeocode/countryCode:method"}}{{/crossLink}} method.
 * @param {Number} [options.limit=10] Maximum number of responses that will be returned
 * @param {Number} [options.offset=0] Starting offset of the returned results within the full result set
 * @param {String} [options.language] Language to use in the response.
 *  The list with all the available codes can be found
 *  <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>.
 * @param {String} [options.streetNumber] Street Number
 * @param {String} [options.streetName] Street name
 * @param {String} [options.crossStreet] Cross street name
 * @param {String} [options.municipality] Municipality (city/town)
 * @param {String} [options.municipalitySubdivision] Municipality subdivision (sub/super city)
 * @param {String} [options.countryTertiarySubdivision] Named area
 * @param {String} [options.countrySecondarySubdivision] County
 * @param {String} [options.countrySubdivision] State or province
 * @param {String} [options.postalCode] Zip or postal code
 *
 * @example
 * ```javascript
 * function callbackFn(result) {
 *   console.log(result);
 * };
 * tomtom.structuredGeocode()
 *   .countryCode("GB")
 *   .callback(callbackFn)
 *   .go();
 * ```
 */

define(['rest/structuredGeocode', 'model/geocode/geocode', 'services/chain', 'services/validators',
    'model/geocode/batchGeocode', 'services/converters', './structureGeocodeParameters'],
function(restStructuredGeocode, model, chain, validators, batchModel, converters, structureGeocodeParameters) {
    'use strict';

    var fields = structureGeocodeParameters(),
        batchFields = {
            batchItems: {
                validator: validators.arrayOf(fields),
                converter: converters.arrayOf(fields)
            }
        };

    var structuredGeocode = restStructuredGeocode();

    function checkBestResult(data) {
        var result = Boolean(data.bestResult);
        delete data.bestResult;
        return result;
    }

    function modifyRequestParams(data) {
        var bestResult = checkBestResult(data);
        if (bestResult) {
            data.limit = 1;
            data.offset = 0;
        }
    }

    function handleBatchChain(data, callback) {
        data.batchItems.forEach(modifyRequestParams);
        structuredGeocode.batch(fields, data, function(results) {
            callback.call(this, batchModel(results));
        });
    }

    function handleChain(data, callback) {
        var bestResult = Boolean(data.bestResult);
        modifyRequestParams(data);
        structuredGeocode.structuredGeocode(fields, data, function(response) {
            if (bestResult) {
                callback.call(this, model(response)[0]);
            } else {
                callback.call(this, model(response));
            }
        });
    }

    return function(options) {
        if (Array.isArray(options)) {
            return chain(batchFields, handleBatchChain, {
                batchItems: options
            });
        } else {
            return chain(fields, handleChain, options);
        }
    };
});



// WEBPACK FOOTER //
// ./services/structureGeocode/structuredGeocode.js